import DataStructure, { sanitize } from "./DataStructure";
import { highlightDescription, itemIcon, itemSummary } from "./SaleItem";
import COLORS from "../colors.js";
import { deepFreeze } from "../util/misc";
import { compact } from "lodash";

// The customer's copy of a sale item, written by functions/customerMirrors.js
// at c/{customerID}/projects/{projectID}/items/{itemID}: what it is and how
// far along it is, never its cost, price or margin.
export const CUSTOMER_PROJECT_ITEM_FIELDS = {
  saleID: "",
  line: 0,
  accessoryIndex: 0,
  image: null,
  parentID: null,
  category: "",
  brand: "",
  series: "",
  location: "",
  description: "",
  quantity: 1,
  quantityReady: 0,
  quantityScheduled: 0,
  quantityDelivered: 0,
  quantityInstalled: 0,
  fullyDelivered: false,
  fullyInstalled: false,
  installedByUs: false,
};
deepFreeze(CUSTOMER_PROJECT_ITEM_FIELDS);

export const CUSTOMER_ITEM_STAGES = {
  ordered: {
    order: 0,
    label: "On order",
    icon: "order",
    color: "info",
  },
  received: {
    order: 1,
    label: "At our warehouse",
    icon: "warehouse",
    color: "info",
  },
  scheduled: {
    order: 2,
    label: "Delivery scheduled",
    icon: "calendar",
    color: "warning",
  },
  deliveredPartial: {
    order: 3,
    label: "Partially delivered",
    icon: "delivered",
    color: "warning",
  },
  delivered: {
    order: 4,
    label: "Delivered",
    icon: "delivered",
    color: "positive",
  },
  installedPartial: {
    order: 5,
    label: "Partially installed",
    icon: "installation",
    color: "warning",
  },
  installed: {
    order: 6,
    label: "Installed",
    icon: "installation",
    color: "positive",
  },
};
deepFreeze(CUSTOMER_ITEM_STAGES);

export default class CustomerProjectItem extends DataStructure {
  constructor(customerID, projectID, id, data = {}) {
    data = sanitize(data, CUSTOMER_PROJECT_ITEM_FIELDS);
    super(data);

    this.customerID = customerID;
    this.projectID = projectID;
    this.id = id;
  }

  get stageType() {
    const {
      quantity,
      quantityReady,
      quantityScheduled,
      quantityDelivered,
      quantityInstalled,
      fullyDelivered,
      fullyInstalled,
      installedByUs,
    } = this.data;
    if (installedByUs && (fullyInstalled || quantityInstalled >= quantity)) {
      return "installed";
    }
    if (installedByUs && quantityInstalled > 0) return "installedPartial";
    if (fullyDelivered || quantityDelivered >= quantity) return "delivered";
    if (quantityDelivered > 0) return "deliveredPartial";
    if (quantityScheduled > 0) return "scheduled";
    if (quantityReady > 0) return "received";
    return "ordered";
  }

  get stage() {
    const type = CUSTOMER_ITEM_STAGES[this.stageType];
    return { ...type, ...COLORS[type.color], type: this.stageType };
  }

  get lineNumber() {
    if (!this.data.line) return null;
    return this.data.accessoryIndex
      ? `${this.data.line}.${this.data.accessoryIndex}`
      : String(this.data.line);
  }

  get icon() {
    return itemIcon(this.data.series);
  }

  get description() {
    return highlightDescription(this.data.description);
  }

  summary(length = 200) {
    return itemSummary(this.data.description, length);
  }

  toString() {
    return compact([
      this.data.brand,
      this.data.series,
      this.data.location,
    ]).join(" · ");
  }

  // Database

  get path() {
    return CustomerProjectItem.getPath(
      this.customerID,
      this.projectID,
      this.id
    );
  }

  static getPath(customerID, projectID, id) {
    let path = `c/${customerID}/projects/${projectID}/items`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(item) {
    return item.data;
  }

  static fromFirestore(snapshot, options) {
    const project = snapshot.ref.parent.parent;
    return new CustomerProjectItem(
      project.parent.parent.id,
      project.id,
      snapshot.id,
      snapshot.data(options)
    );
  }
}
